import { Loader2 } from "lucide-react";
import type { FC } from "react";

import { cn } from "@/lib/utils";

import { byUrgency, groupTone, TONE_CLASSES } from "../signalTone";
import type { AttentionItem } from "../types";

const RING_TONE = {
  red: "border-destructive",
  green: "border-success"
} as const;

/**
 * The one agent signal mark on tabs: a red count when an agent needs you, a
 * green count when one finished, a spinner while one works. `inline` follows
 * a label; `corner` sits on an avatar's corner, ringed in the bar's colour.
 * Pulses twice when the most urgent kind or the count changes.
 */
export const SignalBadge: FC<{
  items: AttentionItem[];
  running?: boolean;
  variant?: "inline" | "corner";
  className?: string;
}> = ({ items, running = false, variant = "inline", className }) => {
  const tone = groupTone(items);
  const corner = variant === "corner";
  if (tone) {
    const top = byUrgency(items)[0];
    return (
      <span
        data-testid="signal-badge"
        data-tone={tone}
        aria-label={
          tone === "red"
            ? `${items.length} need you`
            : `${items.length} finished`
        }
        className={cn(
          "flex h-3 min-w-3 shrink-0 items-center justify-center rounded-full px-0.75 text-xs leading-none font-bold",
          TONE_CLASSES[tone].solid,
          corner
            ? "absolute -top-1 -right-1 ring-cutout ring-shell"
            : "relative",
          className
        )}
      >
        <span
          key={`${top.kind}-${items.length}`}
          aria-hidden
          className={cn(
            "signal-pulse-ring pointer-events-none absolute inset-0 rounded-full border",
            RING_TONE[tone]
          )}
        />
        {items.length}
      </span>
    );
  }
  if (!running) return null;
  return (
    <span
      data-testid="signal-badge"
      data-tone="running"
      aria-label="Agent working"
      className={cn(
        "flex shrink-0 items-center justify-center text-muted-foreground",
        corner && "absolute -right-0.5 -bottom-0.5 rounded-full bg-shell p-px",
        className
      )}
    >
      <Loader2 className="size-2.5 animate-spin" />
    </span>
  );
};
